import { Box, Typography } from "@mui/material";
import { ReactNode } from "react";

import { space, typography } from "../theme/designTokens";

type ActivityRowHeaderProps = {
  // Leading visual: a thumbnail (recommendation rows) or a category-tinted
  // icon tile (procedure sessions). Provided by the caller so each context keeps
  // its own tile while sharing the surrounding layout and title typography.
  leading: ReactNode;
  // Small uppercase label above the title (e.g. "Sitzung 1").
  eyebrow?: string;
  title: string;
  // Optional small icon rendered inline before the title (e.g. content-kind icon).
  titleIcon?: ReactNode;
  // Optional node below the title (e.g. a duration chip).
  caption?: ReactNode;
  // Optional node aligned to the right edge (e.g. a status pill or action button).
  trailing?: ReactNode;
  gap?: number;
  titleWeight?: number;
};

// Shared row header: leading tile + title cluster + optional trailing slot.
// Used by both the recommended-activities rows and the procedure activity
// session cards so the two stay visually consistent.
export const ActivityRowHeader = ({
  leading,
  eyebrow,
  title,
  titleIcon,
  caption,
  trailing,
  gap = space.xs,
  titleWeight = typography.fontWeight.semibold,
}: ActivityRowHeaderProps) => (
  <Box sx={{ display: "flex", alignItems: "center", gap }}>
    {leading}
    <Box sx={{ flex: 1, minWidth: 0 }}>
      {eyebrow && (
        <Typography
          sx={{
            fontSize: typography.fontSize.xxs,
            fontWeight: typography.fontWeight.semibold,
            color: "text.secondary",
            letterSpacing: typography.letterSpacing.md,
            textTransform: "uppercase",
          }}
        >
          {eyebrow}
        </Typography>
      )}
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
        {titleIcon}
        <Typography variant="body1" sx={{ fontWeight: titleWeight, lineHeight: typography.lineHeight.xs, minWidth: 0 }}>
          {title}
        </Typography>
      </Box>
      {caption && <Box sx={{ mt: 0.5 }}>{caption}</Box>}
    </Box>
    {trailing}
  </Box>
);
